<template>
  <ModalTemplate
    :is-open="isOpen"
    :on-close="onClose"
    :title="
      $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CONTACT_CONFIG.MODAL_TITLE')
    "
  >
    <div class="contact_settings_modal">
      <!-- Audience Filters -->
      <section class="step_content">
        <article>
          <label
            class="field_label"
            :class="{ error: $v.appliedFilters.$error }"
          >
            {{
              $t(
                'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD_STEP.ONE_OFF.AUDIENCE_LABEL'
              )
            }}
          </label>
          <div v-if="appliedFilters.length" class="audience_filter">
            <FilterInputBox
              v-for="(filter, i) in appliedFilters"
              :key="i"
              v-model="appliedFilters[i]"
              :filter-groups="filterGroups"
              :grouped-filters="true"
              :input-type="getInputType(appliedFilters[i].attribute_key)"
              :operators="getOperators(appliedFilters[i].attribute_key)"
              :dropdown-values="
                getDropdownValues(appliedFilters[i].attribute_key)
              "
              :show-query-operator="i !== appliedFilters.length - 1"
              :show-user-input="
                showUserInput(appliedFilters[i].filter_operator)
              "
              :v="$v.appliedFilters.$each[i]"
              :disable-all="isLoading"
              re-order-labels="false"
              @resetFilter="resetFilter(i, appliedFilters[i])"
              @removeFilter="removeFilter(i)"
            />
            <div class="filter-actions mt-4">
              <woot-button
                icon="add"
                class="add_campaign_filter"
                color-scheme="primary"
                variant="clear"
                size="small"
                :is-disabled="isLoading"
                @click="appendNewFilter"
              >
                {{ $t('CONTACTS_FILTER.ADD_NEW_FILTER') }}
              </woot-button>
            </div>
          </div>
        </article>
        <AudienceAndTopupAiBroadcast
          v-if="eligible_audience.isFetched"
          :eligible_audience="eligible_audience"
        />
      </section>

      <!-- Navigation buttons -->
      <section class="modal_btns">
        <woot-button class="modal_btn" variant="clear" @click="onClose">
          {{ $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD_STEP.BTNS.CANCEL') }}
        </woot-button>
        <woot-button
          class="modal_btn modal_btn_solid"
          :is-disabled="isLoading"
          :is-loading="isLoading"
          @click="saveSettings"
        >
          {{ $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD_STEP.BTNS.SAVE') }}
        </woot-button>
      </section>
    </div>
  </ModalTemplate>
</template>

<script>
import { mapGetters } from 'vuex';
import { required, requiredIf } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import filterMixin from 'shared/mixins/filterMixin';
import ModalTemplate from 'components/ModalTemplate.vue';
import FilterInputBox from 'components/widgets/FilterInput/Index.vue';
import AudienceAndTopupAiBroadcast from '../broadcastAgent/AudienceAndTopupAiBroadcast.vue';
import contactFilterItems, {
  filterAttributeGroups,
} from '../../../contacts/contactFilterItems/index';
import countries from '../../../../../../shared/constants/countries';
import filterQueryGenerator from '../../../../../helper/filterQueryGenerator';
import CampaignAgentsApi from 'dashboard/api/campaign_agents';
import * as OPERATORS from '../../../../../components/widgets/FilterInput/FilterOperatorTypes';

export default {
  components: {
    ModalTemplate,
    FilterInputBox,
    AudienceAndTopupAiBroadcast,
  },
  mixins: [alertMixin, filterMixin],
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    onSave: {
      type: Function,
      default: () => {},
    },
    prefillData: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      debounceTimeout: null,
      appliedFilters: [],
      filterGroups: [],
      filterTypes: contactFilterItems.map(filter => ({
        ...filter,
        attributeName: this.$t(
          `CONTACTS_FILTER.ATTRIBUTES.${filter.attributeI18nKey}`
        ),
      })),
      attributeModel: 'contact_attribute',
      filtersFori18n: 'CONTACTS_FILTER',
      eligible_audience: {
        audience: 0,
        isFetched: false,
      },
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      contactStatus: 'accounts/getContactStatus',
    }),
    filterGroupsWithNames() {
      return filterAttributeGroups.map(group => ({
        ...group,
        name: this.$t(`CONTACTS_FILTER.GROUPS.${group.i18nGroup}`),
        attributes: group.attributes.map(attr => ({
          ...attr,
          name: this.$t(`CONTACTS_FILTER.ATTRIBUTES.${attr.i18nKey}`),
        })),
      }));
    },
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.initializeModal();
      }
    },
    appliedFilters: {
      deep: true,
      handler() {
        this.debouncedFetchEligibleAudience();
      },
    },
  },
  async mounted() {
    await this.setFilterAttributes();
  },
  validations() {
    return {
      appliedFilters: {
        required,
        $each: {
          values: {
            required: requiredIf(prop => {
              return !(
                prop.filter_operator === 'is_present' ||
                prop.filter_operator === 'is_not_present'
              );
            }),
          },
        },
      },
    };
  },
  methods: {
    async initializeModal() {
      this.isLoading = true;
      try {
        // Set initial filters
        if (this.prefillData?.appliedFilters) {
          this.appliedFilters = [...this.prefillData.appliedFilters];
        } else {
          this.appliedFilters = [];
          this.appliedFilters.push({
            attribute_key: 'name',
            filter_operator: 'equal_to',
            values: '',
            attribute_model: 'standard',
          });
        }

        if (this.prefillData?.eligible_audience) {
          this.eligible_audience = this.prefillData.eligible_audience;
        } else {
          // Fetch audience initially
          await this.checkEligibleAudience();
        }
      } catch (error) {
        console.error('Error initializing modal:', error);
        this.showAlert(
          this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CONTACT_CONFIG.AUDIENCE_ERROR'
          )
        );
      } finally {
        this.isLoading = false;
      }
    },
    async setFilterAttributes() {
      // Fetch custom attributes from store
      let allCustomAttributes = this.$store.getters[
        'attributes/getAttributesByModel'
      ](this.attributeModel);
      if (!allCustomAttributes.length) {
        await this.$store.dispatch('attributes/get');
        allCustomAttributes = this.$store.getters[
          'attributes/getAttributesByModel'
        ](this.attributeModel);
      }

      // Create custom attributes filter group
      const customAttributesGroup = {
        name: this.$t(`${this.filtersFori18n}.GROUPS.CUSTOM_ATTRIBUTES`),
        attributes: allCustomAttributes.map(attr => ({
          key: attr.attribute_key,
          name: attr.attribute_display_name,
          attribute_display_type: attr.attribute_display_type,
          i18nKey: attr.attribute_key,
        })),
      };

      // Combine standard filter groups with custom attributes
      this.filterGroups = [
        ...filterAttributeGroups.map(group => ({
          ...group,
          name: this.$t(`CONTACTS_FILTER.GROUPS.${group.i18nGroup}`),
          attributes: group.attributes.map(attr => ({
            ...attr,
            name: this.$t(`CONTACTS_FILTER.ATTRIBUTES.${attr.i18nKey}`),
          })),
        })),
        customAttributesGroup,
      ];
    },
    getInputType(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      if (type) {
        return type.inputType;
      }
      // Check if it's a custom attribute
      const customAttr = this.filterGroups
        .flatMap(group => group.attributes)
        .find(attr => attr.key === key);
      if (customAttr && customAttr.attribute_display_type) {
        return this.customAttributeInputType(customAttr.attribute_display_type);
      }
      return 'plain_text';
    },
    getOperators(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      if (type) {
        return type.filterOperators;
      }
      // Check if it's a custom attribute
      const customAttr = this.filterGroups
        .flatMap(group => group.attributes)
        .find(attr => attr.key === key);
      if (customAttr && customAttr.attribute_display_type) {
        return this.getOperatorTypes(customAttr.attribute_display_type);
      }
      return [];
    },
    customAttributeInputType(key) {
      switch (key) {
        case 'date':
          return 'date';
        default:
          return 'plain_text';
      }
    },
    getOperatorTypes(key) {
      switch (key) {
        case 'list':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'text':
          return OPERATORS.OPERATOR_TYPES_3;
        case 'number':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'link':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'date':
          return OPERATORS.OPERATOR_TYPES_4;
        case 'checkbox':
          return OPERATORS.OPERATOR_TYPES_1;
        default:
          return OPERATORS.OPERATOR_TYPES_1;
      }
    },
    getDropdownValues(type) {
      switch (type) {
        case 'country_code':
          return countries;
        case 'status_id':
          return this.contactStatus;
        default:
          return undefined;
      }
    },
    showUserInput(operatorType) {
      return !(
        operatorType === 'is_present' || operatorType === 'is_not_present'
      );
    },
    resetFilter(index, currentFilter) {
      this.appliedFilters[index].filter_operator = this.filterTypes.find(
        filter => filter.attributeKey === currentFilter.attribute_key
      ).filterOperators[0].value;
      this.appliedFilters[index].values = '';
      this.checkEligibleAudience();
    },
    removeFilter(index) {
      if (this.appliedFilters.length <= 1) {
        this.showAlert(this.$t('CONTACTS_FILTER.FILTER_DELETE_ERROR'));
      } else {
        this.appliedFilters.splice(index, 1);
        this.checkEligibleAudience();
      }
    },
    appendNewFilter() {
      this.appliedFilters.push({
        attribute_key: 'name',
        filter_operator: 'equal_to',
        values: '',
        query_operator: 'and',
        attribute_model: 'standard',
      });
      this.checkEligibleAudience();
    },
    debouncedFetchEligibleAudience() {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }
      this.debounceTimeout = setTimeout(() => {
        this.checkEligibleAudience();
      }, 500);
    },
    async checkEligibleAudience() {
      try {
        this.isLoading = true;
        const { data } = await CampaignAgentsApi.checkEligibleAudience({
          account_id: this.currentUser.account_id,
          campaign_agent: {
            audience: filterQueryGenerator(this.appliedFilters).payload,
          },
        });
        this.eligible_audience = {
          audience: data.audience,
          isFetched: true,
        };
      } catch (error) {
        console.error('Error checking eligible audience:', error);
        this.showAlert(
          this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CONTACT_CONFIG.AUDIENCE_ERROR'
          )
        );
      } finally {
        this.isLoading = false;
      }
    },
    saveSettings() {
      const settings = {
        appliedFilters: this.appliedFilters,
        eligible_audience: this.eligible_audience,
      };
      this.onSave(settings);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/widgets/_fonts.scss';
@import '~dashboard/assets/scss/woot';

.contact_settings_modal {
  padding: 24px;
}

.step_content {
  min-height: 200px;
}

.field_label {
  font-weight: 500;
  font-size: 12px;
  margin-bottom: 8px;
  color: var(--s-700);

  &.error {
    color: var(--r-500);
  }
}

.audience_filter {
  margin-bottom: 16px;
}

.filter-actions {
  margin-top: 12px;
}

.modal_btns {
  display: flex;
  gap: 16px;
  margin-top: 24px;
  justify-content: flex-end;

  .modal_btn {
    min-width: 140px;
  }

  .modal_btn_solid {
    background-color: var(--w-500);
  }
}
</style>

<style lang="scss">
.audience_filter .filters .filter {
  padding: 0;
  border: none;
  background-color: #fff;
}

.audience_filter .filters .filter .filter-inputs .filter__question,
.audience_filter .filters .filter .filter-inputs .filter__operator {
  margin-right: 12px;
}

.add_campaign_filter svg,
.add_campaign_filter .button__content {
  font-family: 'Figtree', sans-serif;
  line-height: 1.4;
  font-weight: 600;
  font-size: 14px;
  color: var(--w-500);
}

.add_campaign_filter:hover .button__content {
  color: inherit;
}
</style>
